import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/common";

const HERO = "https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("email");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState(null);

  if (user) return <Navigate to="/" replace />;

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setWarn(null);
    try {
      const { data } = await api.post("/auth/request-code", { email: email.trim() });
      if (!data.sent) setWarn(data.error || "Email delivery failed. Contact your administrator.");
      else toast.success("If this email is registered, a code is on its way");
      setStep("code");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value) => {
    const c = value ?? code;
    if (c.length !== 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify", { email: email.trim(), code: c });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name || data.user.email}`);
      nav("/");
    } catch (err) {
      toast.error(errMsg(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Total Quality Insurance operations center" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b0f17] via-[#0b0f17]/70 to-[#0b0f17]/30" />
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-md bg-blue-600 inline-flex items-center justify-center shadow-[0_0_30px_-4px_rgba(59,130,246,0.7)]">
              <Phone className="h-5 w-5 text-white" />
            </span>
            <div>
              <div className="font-display font-extrabold text-white text-lg leading-none">TQI Pulse</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mt-1">Conversation Intelligence</div>
            </div>
          </div>
          <div className="max-w-lg space-y-6">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.02] tracking-tight">
              Every call coached.
              <br />
              <span className="text-blue-400">Every text answered.</span>
            </h1>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
              AI reviews of OpenPhone conversations, SMS response SLAs with Slack and email escalation, anomaly detection and automated leadership reports — for the Total Quality Insurance team.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-2">
              {[
                ["GPT-5.4", "Coaching engine"],
                ["< 30 min", "Text SLA default"],
                ["24 / 7", "Pattern monitoring"],
              ].map(([v, l]) => (
                <div key={l} className="panel p-3">
                  <div className="metric text-lg">{v}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs font-mono text-slate-500">Total Quality Insurance · Augusta · Charleston</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-8 fade-up">
          <div className="lg:hidden flex items-center gap-3">
            <span className="h-9 w-9 rounded-md bg-blue-600 inline-flex items-center justify-center">
              <Phone className="h-4 w-4 text-white" />
            </span>
            <div className="font-display font-extrabold text-white">TQI Pulse</div>
          </div>
          <div>
            <div className="eyebrow mb-2">Secure sign in</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">{step === "email" ? "Sign in with a one-time code" : "Enter your 6-digit code"}</h2>
            <p className="text-sm text-slate-400 mt-2">
              {step === "email" ? "We'll email you a code — no passwords to remember." : <>If <span className="text-slate-200 font-medium">{email}</span> is registered, a code was sent. It expires in 10 minutes.</>}
            </p>
          </div>

          {step === "email" ? (
            <form onSubmit={requestCode} className="space-y-4" data-testid="login-email-form">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input data-testid="login-email-input" type="email" required autoFocus placeholder="you@totalqualityinsurance.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 pl-10 bg-[#0f172a] border-[#2d3d58] text-base" />
              </div>
              <Button data-testid="login-send-code-button" type="submit" disabled={busy || !email} className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white font-semibold">
                {busy ? <Spinner className="text-white" /> : <>Email me a code <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          ) : (
            <div className="space-y-5" data-testid="login-code-form">
              {warn && <div data-testid="login-email-warning" className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md p-3">{warn}</div>}
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} data-testid="login-otp-input" autoFocus>
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-14 w-12 text-2xl font-mono rounded-md border border-[#2d3d58] bg-[#0f172a] first:rounded-md last:rounded-md" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button data-testid="login-verify-button" onClick={() => verify()} disabled={busy || code.length !== 6} className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white font-semibold">
                {busy ? <Spinner className="text-white" /> : <>Verify & continue <ShieldCheck className="h-4 w-4 ml-2" /></>}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button data-testid="login-change-email-button" className="text-slate-400 hover:text-white" onClick={() => { setStep("email"); setCode(""); }}>Use a different email</button>
                <button data-testid="login-resend-code-button" className="text-blue-400 hover:text-blue-300" onClick={requestCode} disabled={busy}>Resend code</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
